import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { useUser, UserRole } from '../contexts/UserContext';

// Helper to get dashboard route based on role
const getDashboardRoute = (role: UserRole): string => {
  switch (role) {
    case 'mentor':
      return '/mentor/dashboard';
    default:
      return '/mentors';
  }
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useUser();
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [mode, setMode] = useState<'signin' | 'register'>('signin');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPromoField, setShowPromoField] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoMessage, setPromoMessage] = useState<{ text: string; success: boolean } | null>(null);
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  // Check if user came from "Have a promotion code?" link
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('promo') === 'true') {
      setShowPromoField(true);
    }
  }, [location]);

  const handleApplyPromo = async (token?: string) => {
    if (!promoCode.trim()) {
      setPromoMessage({ text: 'Enter a code to apply it.', success: false });
      return;
    }

    setIsApplyingPromo(true);
    setPromoMessage(null);

    try {
      const authToken = token || pendingToken;
      const response = await axios.post(
        `${API_URL}/promo-codes/redeem`,
        { code: promoCode.trim().toUpperCase() },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        const trialDate = new Date(response.data.trialEndDate).toLocaleDateString();
        setPromoMessage({
          text: `Code applied — extended to ${trialDate} (+${response.data.daysAdded} days).`,
          success: true,
        });
        setPromoCode('');
      }
    } catch (error: any) {
      setPromoMessage({
        text: error.response?.data?.error || 'Invalid or expired promo code',
        success: false,
      });
    } finally {
      setIsApplyingPromo(false);
    }
  };

  const completeSignIn = async (token: string, user: any) => {
    // Apply promo code if user entered one
    if (promoCode.trim()) {
      await handleApplyPromo(token);
    }

    // Check if signing up with a specific role (e.g. from become-a-mentor)
    const params = new URLSearchParams(location.search);
    const requestedRole = params.get('role') as UserRole | null;

    let userData = user;

    if (requestedRole && ['mentor', 'counselor'].includes(requestedRole)) {
      try {
        const addRoleResponse = await axios.post(
          `${API_URL}/auth/roles/add`,
          { role: requestedRole },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        if (addRoleResponse.data.success) {
          userData = addRoleResponse.data.user;
        }
      } catch (roleError) {
        console.error('Error adding role:', roleError);
        // Continue with login even if role addition fails
      }
    }

    setPendingToken(token);

    // Get roles from response (new system) or parse from userType (legacy)
    const userRoles: UserRole[] =
      userData.roles && userData.roles.length > 0
        ? userData.roles
        : userData.userType
        ? userData.userType
            .split(',')
            .map((r: string) => r.trim())
            .filter((r: string) => r)
        : ['parent'];

    const updatedUser = {
      ...userData,
      userType: userData.userType || userRoles.join(','),
      roles: userRoles,
    };
    login(token, updatedUser);

    // Started booking before signing in? Resume it.
    const intendedIntroCall = sessionStorage.getItem('intendedIntroCall');
    const intendedBooking = sessionStorage.getItem('intendedBooking');
    if (intendedIntroCall) {
      sessionStorage.removeItem('intendedIntroCall');
      navigate(`/intro-call/${intendedIntroCall}`);
    } else if (intendedBooking) {
      const bookingData = JSON.parse(intendedBooking);
      sessionStorage.removeItem('intendedBooking');
      navigate('/mentor-booking', { state: bookingData });
    } else if (userRoles.length > 1) {
      navigate('/select-role');
    } else {
      navigate(getDashboardRoute(userRoles[0]));
    }
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/google`,
        { credential: credentialResponse.credential },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data.success) {
        await completeSignIn(response.data.token, response.data.user);
      }
    } catch (error: any) {
      console.error('Google login error:', error);
      alert('Failed to sign in with Google. Please try again.');
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setSubmitting(true);
    try {
      const params = new URLSearchParams(location.search);
      const requestedRole = params.get('role');
      const endpoint = mode === 'register' ? 'register' : 'login';
      const payload =
        mode === 'register'
          ? {
              name: name.trim(),
              email: email.trim(),
              password,
              userType: requestedRole === 'mentor' ? 'mentor' : 'parent',
            }
          : { email: email.trim(), password };

      const response = await axios.post(`${API_URL}/auth/${endpoint}`, payload);
      if (response.data.success) {
        await completeSignIn(response.data.token, response.data.user);
      }
    } catch (error: any) {
      setAuthError(error.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleError = () => {
    console.error('Google login failed');
    alert('Google sign-in failed. Please try again.');
  };

  return (
    <div
      className="section"
      style={{
        background: 'var(--warm)',
        minHeight: 'calc(100vh - 200px)',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div className="wrap" style={{ width: '100%' }}>
        <div style={{ maxWidth: 420, margin: '0 auto' }}>
          <div className="card" style={{ padding: 36 }}>
            <span className="eyebrow" style={{ display: 'block', marginBottom: 10 }}>
              {mode === 'signin' ? 'Sign in' : 'Create account'}
            </span>
            <h1 style={{ fontSize: 26, marginBottom: 8 }}>
              {mode === 'signin' ? 'Welcome back' : 'Create your account'}
            </h1>
            <p className="meta" style={{ marginBottom: 24 }}>
              One account for booking, sessions, and messages. Parents own the account — kids
              under 13 never hold one.
            </p>

            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                size="large"
                theme="outline"
                text="continue_with"
                shape="rectangular"
                width="320"
              />
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                margin: '4px 0 14px',
                color: 'var(--ink-secondary)',
                fontSize: 12,
              }}
            >
              <span style={{ flex: 1, borderTop: '1px solid var(--hairline)' }} />
              or
              <span style={{ flex: 1, borderTop: '1px solid var(--hairline)' }} />
            </div>

            <form onSubmit={handlePasswordSubmit}>
              {mode === 'register' && (
                <div className="field">
                  <label htmlFor="auth-name">Name</label>
                  <input
                    id="auth-name"
                    type="text"
                    placeholder="Your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
              )}
              <div className="field">
                <label htmlFor="auth-email">Email</label>
                <input
                  id="auth-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="auth-password">Password</label>
                <input
                  id="auth-password"
                  type="password"
                  placeholder={mode === 'register' ? 'At least 6 characters' : 'Your password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                  minLength={6}
                  required
                />
              </div>
              {authError && (
                <p className="meta" style={{ color: 'var(--cardinal)', marginBottom: 12 }}>
                  {authError}
                </p>
              )}
              <button className="btn btn-primary btn-block" type="submit" disabled={submitting}>
                {submitting
                  ? mode === 'signin'
                    ? 'Signing in…'
                    : 'Creating account…'
                  : mode === 'signin'
                  ? 'Sign in'
                  : 'Create account'}
              </button>
            </form>

            <p className="meta" style={{ textAlign: 'center', marginTop: 12 }}>
              {mode === 'signin' ? (
                <>
                  No account yet?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setMode('register');
                      setAuthError(null);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      font: 'inherit',
                      color: 'var(--ink)',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    Create one with email
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setMode('signin');
                      setAuthError(null);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      font: 'inherit',
                      color: 'var(--ink)',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    Sign in
                  </button>
                </>
              )}
            </p>

            <button
              className="btn btn-ghost btn-sm btn-block"
              style={{ color: 'var(--ink-secondary)' }}
              onClick={() => setShowPromoField((v) => !v)}
            >
              Have a promotion code?
            </button>
            {showPromoField && (
              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    placeholder="e.g. ROBOTICS25"
                    style={{ flex: 1 }}
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    maxLength={20}
                  />
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleApplyPromo()}
                    disabled={isApplyingPromo}
                  >
                    {isApplyingPromo ? 'Applying…' : 'Apply'}
                  </button>
                </div>
                <p
                  className="help"
                  style={promoMessage && !promoMessage.success ? { color: 'var(--cardinal)' } : undefined}
                >
                  {promoMessage
                    ? promoMessage.text
                    : pendingToken
                    ? 'Signed in — apply your code now.'
                    : 'Codes extend your free intro-call allowance. Applied at sign-in.'}
                </p>
              </div>
            )}

            <div style={{ borderTop: '1px solid var(--hairline)', margin: '22px 0 16px' }}></div>
            <p className="meta">
              New here?{' '}
              <Link to="/family-signup" style={{ textDecoration: 'underline', color: 'var(--ink)' }}>
                Create a family account
              </Link>{' '}
              or{' '}
              <Link to="/become-a-mentor" style={{ textDecoration: 'underline', color: 'var(--ink)' }}>
                apply as a mentor
              </Link>
              .
            </p>
          </div>
          <p className="meta" style={{ textAlign: 'center', marginTop: 16 }}>
            Started booking before signing in? Your session request is saved and resumes after
            sign-in.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
